import { isOutsidePath, type ModelView } from '../../../core/domain/model';
import { machineRows, type MachineCell, type MachineRow } from './machine-rows';

/*
 * The Diagram view of the Machines tile, as data. The same group as the List view (`machine-rows.ts`),
 * drawn as a graph: the outside inputs on the left, the machines of the group in the middle, and the
 * outputs on the right. No Lit and no i18n here: the component gives the words and draws the wires,
 * and a node test can call this.
 *
 * The machines go in 3 columns around the root. A child that the root drives (an input with no
 * wiring, which the parent assigns) goes after the root. Every other child goes before it. The edges
 * come from the wiring alone. The descriptor does not say which outputs of a child the root reads,
 * so each output of a child that is not wired to the PLC is drawn to the root: the group reads it.
 */

/** What a wire carries, which gives it its color: an outside value, a value inside the group, a PLC write, or a published output. */
export type EdgeKind = 'input' | 'internal' | 'plc' | 'exposed';

/** An outside path that a machine of the group reads. `{i}` and `{n}` stay in an array's path, as written. */
export interface GraphInput {
  id: string;
  path: string;
}

/** A PLC symbol that a machine writes, with its safe value, or an output that the root publishes. */
export interface GraphOutput {
  id: string;
  kind: 'plc' | 'exposed';
  /** The PLC path; '' for a published output. */
  path: string;
  /** The published output's name; '' for a PLC output. */
  name: string;
  /** A PLC output's safe value, rendered as text. */
  safe?: string;
}

/** The root or a child, with its live state. An array child is 1 node with its machines. */
export interface GraphMachine {
  id: string;
  name: string;
  root: boolean;
  /** The template's name; '' for the root. */
  template: string;
  /** The active state ids, in the runtime's order. Empty when the model is not active. */
  states: string[];
  fault: string;
  /** The inputs fed by a constant, with the constant as text. */
  constants: { name: string; value: string }[];
  /** Present for an array child only. */
  array?: { count: number; running: number; faulted: number; machines: MachineCell[] };
}

export interface GraphEdge {
  from: string;
  to: string;
  /** The port names that the wire carries, joined by ', '. */
  label: string;
  kind: EdgeKind;
}

export interface MachineGraph {
  inputs: GraphInput[];
  /** The children that the root does not drive, in the descriptor's order. */
  before: GraphMachine[];
  root: GraphMachine;
  /** The children that the root drives, in the descriptor's order. */
  after: GraphMachine[];
  outputs: GraphOutput[];
  edges: GraphEdge[];
  /** The root, each single child, and each machine of each array. */
  machineCount: number;
}

/** The machines of a group: the root, each single child, and each machine of each array. */
export const machineCountOf = (m: Pick<ModelView, 'children'>): number => 1 + (m.children ?? []).reduce((n, c) => n + (c.count ?? 1), 0);

export const ROOT_ID = 'root';
export const childId = (name: string): string => `child:${name}`;
export const inputId = (path: string): string => `in:${path}`;
export const plcId = (path: string): string => `out:${path}`;
export const exposedId = (name: string): string => `exp:${name}`;

const statesOf = (state: string): string[] =>
  state
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

function machineOf(r: MachineRow, id: string): GraphMachine {
  const node: GraphMachine = {
    id,
    name: r.name,
    root: r.root,
    template: r.template,
    states: statesOf(r.state),
    fault: r.fault,
    constants: r.inputs.filter((p) => p.constant !== undefined).map((p) => ({ name: p.name, value: p.constant ?? '' })),
  };
  if (r.array) {
    node.array = { count: r.array.count, running: r.array.running, faulted: r.array.faulted, machines: r.array.machines ?? [] };
  }
  return node;
}

/**
 * The graph of a group. `active` is `isModelActive(m.status)`. Each array is open, so its node has a
 * cell for each machine.
 */
export function machineGraph(m: ModelView, active: boolean): MachineGraph {
  const arrays = new Set(m.children.filter((c) => c.count !== undefined).map((c) => c.name));
  const [rootRow, ...childRows] = machineRows(m, active, arrays);
  const names = new Set(m.children.map((c) => c.name));
  const inputs = new Map<string, GraphInput>();
  const outputs = new Map<string, GraphOutput>();
  const edges = new Map<string, GraphEdge>();

  // 1 wire for each pair of nodes and each kind; 2 ports between the same pair share it.
  const edge = (from: string, to: string, label: string, kind: EdgeKind): void => {
    const key = `${from}|${to}|${kind}`;
    const e = edges.get(key);
    if (!e) edges.set(key, { from, to, label, kind });
    else if (!e.label.split(', ').includes(label)) e.label = `${e.label}, ${label}`;
  };
  const input = (path: string): string => {
    const id = inputId(path);
    if (!inputs.has(id)) inputs.set(id, { id, path });
    return id;
  };
  const plc = (path: string, safe: string | undefined): string => {
    const id = plcId(path);
    if (!outputs.has(id)) outputs.set(id, { id, kind: 'plc', path, name: '', ...(safe !== undefined ? { safe } : {}) });
    return id;
  };

  // The root's own wiring: an outside input, a PLC output, or a published output.
  for (const p of rootRow.inputs) {
    if (p.constant === undefined && isOutsidePath(p.path)) edge(input(p.path), ROOT_ID, p.name, 'input');
  }
  for (const p of rootRow.outputs) {
    if (p.path) {
      edge(ROOT_ID, plc(p.path, p.safe), p.name, 'plc');
    } else {
      const id = exposedId(p.name);
      outputs.set(id, { id, kind: 'exposed', path: '', name: p.name });
      edge(ROOT_ID, id, p.name, 'exposed');
    }
  }

  const before: GraphMachine[] = [];
  const after: GraphMachine[] = [];
  childRows.forEach((r, i) => {
    const id = childId(r.name);
    let driven = false;
    // A name that the wiring gives to another machine: this child reads it, or sends it events.
    for (const [name, other] of Object.entries(m.children[i]?.connections ?? {})) {
      if (other !== r.name) edge(childId(other), id, name, 'internal');
    }
    for (const p of r.inputs) {
      if (p.constant !== undefined) continue;
      if (!p.path) {
        // No wiring: the parent assigns it.
        driven = true;
        edge(ROOT_ID, id, p.name, 'internal');
      } else if (isOutsidePath(p.path)) {
        edge(input(p.path), id, p.name, 'input');
      } else {
        // A connection to another machine of the group, by its name (or `<child>/<port>`).
        const other = p.path.split('/')[0] ?? '';
        if (names.has(other) && other !== r.name) edge(childId(other), id, p.name, 'internal');
      }
    }
    for (const p of r.outputs) {
      if (p.path) edge(id, plc(p.path, p.safe), p.name, 'plc');
      else edge(id, ROOT_ID, p.name, 'internal');
    }
    (driven ? after : before).push(machineOf(r, id));
  });

  return {
    inputs: [...inputs.values()],
    before,
    root: machineOf(rootRow, ROOT_ID),
    after,
    outputs: [...outputs.values()],
    edges: [...edges.values()],
    machineCount: machineCountOf(m),
  };
}
